import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Mail, MessageSquare, RefreshCcw, RotateCcw, Bell } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { extractApiErrorMessage } from '@/lib/api-error';
import { formatDateTime } from '@/lib/utils';
import {
  getFailedNotifications,
  resendNotification,
  type FailedNotification,
} from '@/services/api.client';

const channelIcon = (ch: string) => {
  switch (ch) {
    case 'email':
      return <Mail className="h-4 w-4" />;
    case 'sms':
      return <MessageSquare className="h-4 w-4" />;
    case 'push':
      return <Bell className="h-4 w-4" />;
    default:
      return null;
  }
};

export function SuperAdminNotificationsPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [channelFilter, setChannelFilter] = useState<string | undefined>(undefined);
  const [resendingId, setResendingId] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'notifications-failed', page, channelFilter],
    queryFn: () => getFailedNotifications(page, channelFilter),
  });

  const resendMutation = useMutation({
    mutationFn: resendNotification,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'notifications-failed'] });
      setResendingId(null);
    },
    onError: () => {
      setResendingId(null);
    },
  });

  const handleResend = (id: string) => {
    setResendingId(id);
    resendMutation.mutate(id);
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl text-[var(--text-primary)]">Failed Notifications</h1>
        <p className="text-sm text-[var(--text-secondary)]">
          Notifications that exhausted all retry attempts. You can resend them individually.
        </p>
      </div>

      {/* Filters & Actions */}
      <div className="flex items-center gap-3">
        <select
          className="rounded border border-[var(--border)] bg-[var(--bg-surface)] px-3 py-1.5
                     text-sm text-[var(--text-primary)]"
          value={channelFilter ?? ''}
          onChange={(e) => {
            setChannelFilter(e.target.value || undefined);
            setPage(1);
          }}
        >
          <option value="">All channels</option>
          <option value="email">Email</option>
          <option value="sms">SMS</option>
          <option value="push">Push</option>
        </select>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCcw className="mr-1.5 h-3.5 w-3.5" /> Refresh
        </Button>
      </div>

      {/* Error banner */}
      {isError && (
        <div
          className="flex items-center gap-2 rounded border border-red-300 bg-red-50 px-4 py-3
                        text-sm text-red-800"
        >
          <AlertCircle className="h-4 w-4 shrink-0" />
          {extractApiErrorMessage(error)}
        </div>
      )}

      {resendMutation.isError && (
        <div
          className="flex items-center gap-2 rounded border border-red-300 bg-red-50 px-4 py-3
                        text-sm text-red-800"
        >
          <AlertCircle className="h-4 w-4 shrink-0" />
          Resend failed: {extractApiErrorMessage(resendMutation.error)}
        </div>
      )}

      {resendMutation.isSuccess && (
        <div
          className="flex items-center gap-2 rounded border border-green-300 bg-green-50 px-4
                        py-3 text-sm text-green-800"
        >
          Notification queued for resend successfully.
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded" />
          ))}
        </div>
      )}

      {/* Results */}
      {data?.items.length === 0 && (
        <div
          className="rounded border border-[var(--border)] bg-[var(--bg-surface)] px-6 py-10
                        text-center text-sm text-[var(--text-secondary)]"
        >
          No failed notifications found.
        </div>
      )}

      {data && data.items.length > 0 && (
        <div className="space-y-3">
          {data.items.map((n: FailedNotification) => (
            <div
              key={n.id}
              className="flex items-start justify-between rounded border border-[var(--border)]
                         bg-[var(--bg-surface)] px-4 py-3"
            >
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  {channelIcon(n.channel)}
                  <span className="text-sm font-medium text-[var(--text-primary)]">
                    {n.subject ?? n.template_key ?? 'No subject'}
                  </span>
                  <Badge variant="destructive" className="text-xs">
                    {n.status}
                  </Badge>
                </div>
                <div className="text-xs text-[var(--text-secondary)]">
                  To: {n.user?.email ?? 'Unknown'} · Property: {n.property?.name ?? '–'} · Attempts:{' '}
                  {n.retry_count} · Failed: {n.failed_at ? formatDateTime(n.failed_at) : '–'}
                </div>
                {n.failure_reason && (
                  <div className="text-xs text-red-600">Reason: {n.failure_reason}</div>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={resendingId === n.id || resendMutation.isPending}
                onClick={() => handleResend(n.id)}
              >
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                {resendingId === n.id ? 'Resending...' : 'Resend'}
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <span className="text-xs text-[var(--text-secondary)]">
            Page {data.page} of {data.totalPages} ({data.total} total)
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= data.totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
